import React, { useCallback, useEffect, useRef, useState } from 'react';

import EntryFields from './entry_fields';
import LookupPicker from './lookup_picker';
import { FIELD_ERROR_TEXT } from './adapters';
import { blank_form, pre_save_warnings, validate_form } from './form_logic';
import { LIMITS } from './params';

/*
 * "Add an additional payment". The payment month is the TAB's month, never derived from the entry
 * date (named risk: an adjustment in the wrong month is paid in the wrong month).
 */
const EntryForm = ({
  pay_month,
  month_text,
  categories,
  limits,
  saving,
  error_text,
  reset_token,
  seed,
  error_action,
  now,
  new_request_id,
  search_teachers,
  search_sessions,
  on_submit,
  on_cancel
}) => {
  const [form, setForm] = useState(() => blank_form(pay_month, now(), new_request_id, seed));
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);

  const card_ref = useRef(null);
  const token_seen = useRef(reset_token);
  const focus_teacher = useRef(false);
  // A fresh client_request_id per form instance, regenerated after every successful save so a
  // "Save & add another" cannot replay the previous entry's idempotency key.
  useEffect(() => {
    setForm(blank_form(pay_month, now(), new_request_id, seed));
    setErrors({});
    setTouched(false);
    // A moved entry: the move button is gone, and the category it set is what Ops check next.
    const category = seed && card_ref.current && card_ref.current.querySelector('#tp-ap-category');
    if (category) category.focus();
    // A blank form replacing a saved one ("Save & add another") starts at the teacher.
    else if (reset_token !== token_seen.current) focus_teacher.current = true;
    token_seen.current = reset_token;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset_token, pay_month]);

  // The picker swaps its Change button back for the search input only on the render after the reset.
  useEffect(() => {
    const input = focus_teacher.current && card_ref.current.querySelector('input#tp-ap-teacher');
    if (!input) return;
    focus_teacher.current = false;
    input.focus();
  });

  // Stable per teacher: the picker re-runs its lookup whenever this identity changes.
  const search_teacher_sessions = useCallback(
    (q) => search_sessions(form.teacher_uuid, q),
    [search_sessions, form.teacher_uuid]
  );

  const set_field = useCallback((key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  const submit = (add_another) => {
    const result = validate_form(form, { now: now(), categories, mode: 'create', limits });
    setErrors(result.errors);
    setTouched(true);
    if (!result.ok) return;
    on_submit(form, { add_another });
  };

  const warnings = pre_save_warnings(form, month_text);

  return (
    <div className="tp-card tp-card-overflow" ref={card_ref}>
      <div className="tp-card-hd">
        <span className="tp-card-hd-title">Add an additional payment</span>
        <span className="tp-card-hd-sub">Every entry is money paid TO the teacher</span>
      </div>
      <div className="tp-form">
        <div className="tp-field-2">
          <LookupPicker
            id="tp-ap-teacher"
            name="teacher"
            label="Teacher"
            placeholder="Name or email"
            min_chars={LIMITS.TEACHER_QUERY_MIN_LEN}
            value_text={form.teacher_name}
            search={search_teachers}
            option_key={(t) => t.teacher_uuid}
            render_option={(t) => (
              <span>
                <b>{t.name}</b> · {t.email} · {t.teacher_status}
                {t.has_base_rate === false ? ' · no base rate' : ''}
              </span>
            )}
            on_pick={(t) => {
              set_field('teacher_uuid', t.teacher_uuid);
              set_field('teacher_name', t.name);
              set_field('teacher_has_base_rate', t.has_base_rate === true);
              set_field('related_session_uuid', '');
              set_field('related_session_label', '');
            }}
            on_clear={() => {
              set_field('teacher_uuid', '');
              set_field('teacher_name', '');
              set_field('teacher_has_base_rate', null);
              set_field('related_session_uuid', '');
              set_field('related_session_label', '');
            }}
            error_text={touched && errors.teacher_uuid ? FIELD_ERROR_TEXT[errors.teacher_uuid] : ''}
          />
          <div className="tp-field">
            <label htmlFor="tp-ap-month">Payment month (the month this is paid in)</label>
            <input id="tp-ap-month" className="tp-input" value={month_text} disabled readOnly />
            <span className="tp-hint">
              Set by the month picker at the top of the page — never by the entry date.
            </span>
            {touched && errors.pay_month ? (
              <span className="tp-err">{FIELD_ERROR_TEXT[errors.pay_month]}</span>
            ) : null}
          </div>
        </div>

        <EntryFields
          id_prefix="tp-ap"
          form={form}
          set_field={set_field}
          errors={touched ? errors : {}}
          categories={categories}
          limits={limits}
          search_sessions={search_teacher_sessions}
          session_disabled={!form.teacher_uuid}
        />

        {warnings.map((w) => (
          <div className="tp-banner tp-banner-warn" key={w.code}>
            {w.message}
          </div>
        ))}

        {/* a refusal of this form's own save; other surfaces keep theirs. */}
        {error_text ? (
          <div className="tp-banner tp-banner-error" role="alert">
            {error_text}
            {error_action ? (
              <div>
                <button type="button" className="tp-link" onClick={error_action.run}>
                  {error_action.label}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="tp-modal-ft">
          {/* aria-disabled, not disabled: disabling the focused button drops focus. */}
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={saving ? undefined : on_cancel}
            aria-disabled={saving ? 'true' : undefined}
          >
            Cancel
          </button>
          <button
            type="button"
            className="tp-btn-out"
            onClick={saving ? undefined : () => submit(true)}
            aria-disabled={saving ? 'true' : undefined}
          >
            Save &amp; add another
          </button>
          <button
            type="button"
            className="tp-btn"
            onClick={saving ? undefined : () => submit(false)}
            aria-disabled={saving ? 'true' : undefined}
          >
            {saving ? 'Saving…' : 'Save entry'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EntryForm;
